"use client";

import Image from "next/image";
import { StandaloneAccountMenu } from "@/components/Nav/AccountPopover";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
	AppBar,
	Box,
	Button,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Slide,
	Toolbar,
	useMediaQuery,
	useScrollTrigger,
	useTheme,
} from "@mui/material";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { useAuth } from "@/services/Firebase/Authcontext";
import { getExtensionStoreUrl, getSupportedDesktopBrowser } from "@/lib/extensionChecker";

const HideOnScroll = ({ children }: { children: React.ReactElement }) => {
	const trigger = useScrollTrigger();
	return (
		<Slide appear={false} direction="down" in={!trigger}>
			{children}
		</Slide>
	);
};

const navigationItems = [
	{ label: "Job Tracker", href: "/job-application-tracker" },
	{ label: "Resume", href: "/resume-builder" },
	{ label: "Cover Letter", href: "/motivation-letter-creator" },
	{ label: "Company Status", href: "/company-status" },
	{ label: "Pricing", href: "/pricing" },
];

const StickyHeader = ({ user }: { user?: { uid?: string } }) => {
	const [extensionBrowser, setExtensionBrowser] = useState("Chrome");

	useEffect(() => {
		const browser = getSupportedDesktopBrowser();
		if (browser) setExtensionBrowser(browser === "firefox" ? "Firefox" : browser === "edge" ? "Edge" : "Chrome");
	}, []);
	const [scrolled, setScrolled] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [activeHref, setActiveHref] = useState("");

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const { sessionUser } = useAuth();
	const uid = user?.uid ?? sessionUser?.uid;

	useEffect(() => {
		if (typeof window !== "undefined") {
			setActiveHref(window.location.pathname);
		}
		const handleScroll = () => setScrolled(window.scrollY > 40);
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const mobileDrawer = (
		<Drawer
			anchor="right"
			open={drawerOpen}
			onClose={() => setDrawerOpen(false)}
			PaperProps={{
				sx: {
					width: 270,
					background: "rgba(9,14,30,0.98)",
					backdropFilter: "blur(24px)",
					borderLeft: "1px solid rgba(255,255,255,0.08)",
					color: "#fff",
					display: "flex",
					flexDirection: "column",
				},
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 2.5,
					py: 2,
					borderBottom: "1px solid rgba(255,255,255,0.07)",
				}}
			>
				<Link href={uid ? "/dashboard" : "/"} onClick={() => setDrawerOpen(false)}>
					<Image
						src={theme.palette.mode === "dark" ? "/images/logo_white.png" : "/images/logo.png"}
						alt="Jobstacky"
						width={130}
						height={36}
						style={{ objectFit: "contain" }}
						priority
					/>
				</Link>
				<IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "rgba(255,255,255,0.7)" }}>
					<CloseRoundedIcon />
				</IconButton>
			</Box>

			<List sx={{ flex: 1, px: 1.5, pt: 1.5 }}>
				{navigationItems.map((item) => (
					<ListItem key={item.href} disablePadding sx={{ mb: 0.5 }}>
						<ListItemButton
							component={Link}
							href={item.href}
							onClick={() => setDrawerOpen(false)}
							selected={activeHref === item.href}
							sx={{
								borderRadius: "10px",
								"&.Mui-selected": {
									background: "rgba(96,165,250,0.12)",
									"& .MuiListItemText-primary": { color: "#93c5fd", fontWeight: 600 },
								},
								"&:hover": { background: "rgba(255,255,255,0.06)" },
							}}
						>
							<ListItemText
								primary={item.label}
								primaryTypographyProps={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.85)" }}
							/>
						</ListItemButton>
					</ListItem>
				))}
			</List>

			<Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mx: 2 }} />

			<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
				{uid ? (
					<Button
						component={Link}
						href="/dashboard"
						onClick={() => setDrawerOpen(false)}
						fullWidth
						variant="outlined"
						sx={{
							borderColor: "rgba(255,255,255,0.2)",
							color: "#fff",
							textTransform: "none",
							borderRadius: "10px",
							fontWeight: 600,
							"&:hover": { borderColor: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.05)" },
						}}
					>
						Dashboard
					</Button>
				) : (
					<>
						<Button
							component={Link}
							href="/login"
							onClick={() => setDrawerOpen(false)}
							fullWidth
							variant="outlined"
							sx={{
								borderColor: "rgba(255,255,255,0.2)",
								color: "#fff",
								textTransform: "none",
								borderRadius: "10px",
								fontWeight: 600,
								"&:hover": { borderColor: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.05)" },
							}}
						>
							Log In
						</Button>
						<Button
							component={Link}
							href="/signup"
							onClick={() => setDrawerOpen(false)}
							fullWidth
							variant="contained"
							sx={{
								textTransform: "none",
								borderRadius: "10px",
								fontWeight: 700,
								background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
								boxShadow: "0 4px 18px rgba(99,102,241,0.3)",
								"&:hover": {
									background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
									transform: "translateY(-1px)",
								},
								transition: "all 0.25s ease",
							}}
						>
							Sign Up Free
						</Button>
					</>
				)}
			</Box>
		</Drawer>
	);

	return (
		<>
			<HideOnScroll>
				<AppBar
					position="sticky"
					elevation={0}
					sx={{
						transition: "background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease",
						background: scrolled ? "rgba(9,14,30,0.92)" : "rgba(9,14,30,0.75)",
						backdropFilter: "blur(24px) saturate(180%)",
						WebkitBackdropFilter: "blur(24px) saturate(180%)",
						borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
						boxShadow: scrolled ? "0 8px 32px rgba(0,0,0,0.25)" : "none",
					}}
				>
					<Toolbar
						sx={{
							minHeight: { xs: 64, md: 68 },
							px: { xs: 2, md: 4 },
							display: "grid",
							gridTemplateColumns: "1fr auto 1fr",
							alignItems: "center",
							gap: 2,
						}}
					>
						{/* Logo – left */}
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
							<Link
								href={uid ? "/dashboard" : "/"}
								style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
							>
								<Image
									src={theme.palette.mode === "dark" ? "/images/logo_white.png" : "/images/logo.png"}
									alt="Jobstacky logo"
									width={150}
									height={40}
									priority
									style={{ objectFit: "contain" }}
								/>
							</Link>
						</Box>

						{/* Nav – center (desktop only) */}
						{!isMobile ? (
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								{navigationItems.map((item) => (
									<Link key={item.href} href={item.href} style={{ textDecoration: "none" }}>
										<Button
											sx={{
												color: activeHref === item.href ? "#fff" : "rgba(255,255,255,0.7)",
												textTransform: "none",
												fontSize: "0.9rem",
												fontWeight: activeHref === item.href ? 600 : 400,
												px: 1.5,
												py: 0.75,
												borderRadius: "8px",
												position: "relative",
												"&::after": {
													content: '""',
													position: "absolute",
													bottom: 4,
													left: "50%",
													width: "60%",
													height: "2px",
													borderRadius: "2px",
													background: "linear-gradient(90deg,#60a5fa,#818cf8)",
													transform: activeHref === item.href
														? "translateX(-50%) scaleX(1)"
														: "translateX(-50%) scaleX(0)",
													transition: "transform 0.25s ease",
													transformOrigin: "center",
												},
												"&:hover": {
													color: "#fff",
													background: "rgba(255,255,255,0.07)",
													"&::after": { transform: "translateX(-50%) scaleX(1)" },
												},
												transition: "color 0.2s ease, background 0.2s ease",
											}}
										>
											{item.label}
										</Button>
									</Link>
								))}
							</Box>
						) : (
							<Box />
						)}

						{/* Auth – right */}
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
							{isMobile ? (
								<>
									{uid && <StandaloneAccountMenu />}
									<IconButton
										onClick={() => setDrawerOpen(true)}
										aria-label="Open menu"
										sx={{
											color: "rgba(255,255,255,0.85)",
											border: "1px solid rgba(255,255,255,0.15)",
											borderRadius: "10px",
											p: 0.75,
											"&:hover": { background: "rgba(255,255,255,0.08)" },
										}}
									>
										<MenuRoundedIcon />
									</IconButton>
								</>
							) : uid ? (
								<>
									<Link href="/dashboard" style={{ textDecoration: "none" }}>
										<Button
											variant="outlined"
											sx={{
												color: "rgba(255,255,255,0.85)",
												borderColor: "rgba(255,255,255,0.2)",
												textTransform: "none",
												fontWeight: 600,
												fontSize: "0.88rem",
												borderRadius: "10px",
												px: 2,
												minWidth: 78,
												whiteSpace: "nowrap",
												flexShrink: 0,
												"&:hover": {
													borderColor: "rgba(255,255,255,0.5)",
													background: "rgba(255,255,255,0.06)",
												},
											}}
										>
											Dashboard
										</Button>
									</Link>
									<StandaloneAccountMenu />
								</>
							) : (
								<>
									<Button
										variant="text"
										onClick={() =>
											window.open(
											getExtensionStoreUrl(),
												"_blank",
											)
										}
										sx={{
											color: "rgba(255,255,255,0.65)",
											textTransform: "none",
											fontSize: "0.88rem",
											whiteSpace: "nowrap",
											"&:hover": { color: "#fff", background: "rgba(255,255,255,0.05)" },
										}}
									>
									Add to {extensionBrowser}
									</Button>
									<Link href="/login" style={{ textDecoration: "none" }}>
										<Button
											variant="outlined"
											sx={{
												color: "rgba(255,255,255,0.85)",
												borderColor: "rgba(255,255,255,0.2)",
												textTransform: "none",
												fontWeight: 600,
												borderRadius: "10px",
												fontSize: "0.88rem",
												px: 2,
												minWidth: 78,
												whiteSpace: "nowrap",
												flexShrink: 0,
												"&:hover": {
													borderColor: "rgba(255,255,255,0.5)",
													background: "rgba(255,255,255,0.06)",
												},
											}}
										>
											Log In
										</Button>
									</Link>
									<Link href="/signup" style={{ textDecoration: "none" }}>
										<Button
											variant="contained"
											sx={{
												textTransform: "none",
												fontWeight: 700,
												fontSize: "0.92rem",
												borderRadius: "10px",
												px: 2.5,
												py: 1,
												whiteSpace: "nowrap",
												background: "linear-gradient(135deg,#3b82f6 0%,#6366f1 100%)",
												boxShadow: "0 4px 18px rgba(99,102,241,0.3)",
												"&:hover": {
													background: "linear-gradient(135deg,#2563eb 0%,#4f46e5 100%)",
													boxShadow: "0 6px 24px rgba(99,102,241,0.5)",
													transform: "translateY(-1px)",
												},
												transition: "all 0.25s ease",
											}}
										>
											Sign Up Free
										</Button>
									</Link>
								</>
							)}
						</Box>
					</Toolbar>
				</AppBar>
			</HideOnScroll>

			{mobileDrawer}
		</>
	);
};

export default StickyHeader;
